import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import logo from "../../assets/images/logo.png";
import Seo from "../../components/Seo";
import { Avatar } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, lawyerService, plainText } from "../../services/lawyerApi";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning,";
  if (h < 17) return "Good Afternoon,";
  return "Good Evening,";
};

const formatAmount = (value) => Number((Number(value) || 0).toFixed(2)).toLocaleString();

const capBadge = (n) => (n > 99 ? "99+" : n);

const LawyerDashboard = () => {
  const { profile, setProfile } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingOnline, setTogglingOnline] = useState(false);

  useEffect(() => {
    let active = true;
    lawyerService
      .getDashboard()
      .then((res) => {
        if (!active) return;
        if (res?.data?.status) {
          setStats(res.data.data?.stats || {});
          setActivities(res.data.data?.recent_activities || []);
        } else {
          toast.error(res?.data?.message || "Unable to load dashboard.");
        }
      })
      .catch((err) => active && toast.error(apiErrorMessage(err)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const isOnline = Number(profile?.is_online) === 1;

  const toggleOnline = async () => {
    if (togglingOnline) return;
    const next = !isOnline;
    setTogglingOnline(true);
    setProfile((p) => ({ ...p, is_online: next ? 1 : 0 }));
    try {
      const res = await lawyerService.setOnline(next);
      if (res?.data?.status) {
        toast.success(res.data.message);
      } else {
        throw new Error(res?.data?.message);
      }
    } catch (err) {
      setProfile((p) => ({ ...p, is_online: next ? 0 : 1 }));
      toast.error(apiErrorMessage(err, err?.message || "Unable to update status."));
    } finally {
      setTogglingOnline(false);
    }
  };

  const unreadNotifications = Number(profile?.unread_notifications) || 0;
  const requestCount = Number(profile?.question_unread) || 0;
  const clientCount = Number(stats?.total_clients) || 0;
  const newClients = Number(stats?.new_purchases) || 0;

  return (
    <div className="lwp-page lwp-dashboard">
      <Seo title="Dashboard | Legal Platform" noindex />

      <header className="lwp-topbar">
        <Link to="/lawyer/profile" className="lwp-user">
          <Avatar src={profile?.picture} alt={profile?.name || "Profile"} size={64} className="lwp-avatar" />
          <span className="lwp-user-text">
            <small>{greeting()}</small>
            <strong>{profile?.name || " "}</strong>
          </span>
        </Link>

        <div className="lwp-topbar-actions">
          <label className="lwp-switch">
            <input
              type="checkbox"
              checked={isOnline}
              onChange={toggleOnline}
              disabled={togglingOnline || !profile}
            />
            <span className="lwp-switch-track" />
            <span className="lwp-switch-label">{isOnline ? "Online" : "Offline"}</span>
          </label>

          <Link to="/lawyer/notifications" className="lwp-bell" aria-label="Notifications">
            <i className="bi bi-bell-fill" />
            {unreadNotifications > 0 && (
              <span className="lwp-count">{capBadge(unreadNotifications)}</span>
            )}
          </Link>
        </div>
      </header>

      {Number(profile?.is_approved) === 0 && (
        <div className="lwp-approval-banner mt-3" role="status">
          <i className="bi bi-hourglass-split" />
          <div>
            <strong>Account under approval</strong>
            <p>
              Our team is reviewing your details and documents. You&apos;ll get an email once it&apos;s approved — until
              then calls, chats and clients are locked.
            </p>
          </div>
        </div>
      )}

      <h2 className="lwp-headline">Your success starts</h2>

      <section className="lwp-cards">
        <div className="lwp-earning-card">
          <div className="lwp-earning-top">
            <div>
              <p>My Earning</p>
              <h3>{loading ? <span className="lwp-skel lwp-skel-amount" /> : `$ ${formatAmount(stats?.total_earning)}`}</h3>
            </div>
            <span className="lwp-earning-icon">
              <i className="bi bi-cash-stack" />
            </span>
          </div>
          <Link to="/lawyer/earnings" className="lwp-earning-btn">
            View More
          </Link>
        </div>

        <div className="lwp-stack">
          <Link to="/lawyer/requests" className="lwp-tile">
            <span>My Requests</span>
            <span className="lwp-tile-icon">
              <i className="bi bi-patch-question-fill" />
              {requestCount > 0 && <span className="lwp-count">{capBadge(requestCount)}</span>}
            </span>
          </Link>
          <Link to="/lawyer/clients" className="lwp-tile">
            <span>My Clients{loading ? "" : `(${clientCount})`}</span>
            <span className="lwp-tile-icon">
              <i className="bi bi-person-fill" />
              {newClients > 0 && <span className="lwp-count">{capBadge(newClients)}</span>}
            </span>
          </Link>
        </div>
      </section>

      <section className="lwp-activities">
        <div className="lwp-section-head">
          <h3>Recent Activities</h3>
          <Link to="/lawyer/notifications">View All</Link>
        </div>

        {loading ? (
          <ul className="lwp-activity-list">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="lwp-activity">
                <span className="lwp-activity-avatar lwp-skel" />
                <div className="lwp-activity-body">
                  <span className="lwp-skel lwp-skel-line" />
                  <span className="lwp-skel lwp-skel-line short" />
                </div>
              </li>
            ))}
          </ul>
        ) : activities.length === 0 ? (
          <p className="lwp-empty">No recent activity yet.</p>
        ) : (
          <ul className="lwp-activity-list">
            {activities.map((a) => (
              <li key={a.id} className={`lwp-activity${Number(a.is_read) === 0 ? " unread" : ""}`}>
                <span className="lwp-activity-avatar">
                  <img src={logo} alt="" />
                </span>
                <div className="lwp-activity-body">
                  <p className="lwp-activity-title">{a.title}</p>
                  <p className="lwp-activity-msg">{plainText(a.message)}</p>
                </div>
                <time className="lwp-activity-time" dateTime={a.created_at}>
                  {a.created_at ? moment(a.created_at).fromNow() : ""}
                </time>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default LawyerDashboard;
